import React, { useEffect, useRef, useState } from 'react';

export interface TruncatedSelectOption {
  value: string;
  label: string;
}

interface TruncatedSelectProps {
  value: string;
  options: TruncatedSelectOption[];
  onChange: (value: string) => void;
  widthClass?: string; // e.g. "w-72"
  placeholder?: string;
}

/**
 * A custom dropdown that reliably truncates the selected label with an ellipsis
 * (native <select> does not clip its own selected-value text against padding in
 * Chrome, so long labels run under the chevron). Renders a button + a menu.
 */
const TruncatedSelect: React.FC<TruncatedSelectProps> = ({
  value, options, onChange, widthClass = 'w-72', placeholder = 'Select…',
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={`relative ${widthClass}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-1 pl-2 pr-2 py-1 text-xs border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <span className="truncate text-left">{selected ? selected.label : placeholder}</span>
        <svg className="w-3 h-3 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-2 py-1.5 text-xs hover:bg-gray-100 ${o.value === value ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TruncatedSelect;
